/**
 * Branding Context
 *
 * Provides organization branding settings throughout the app and
 * dynamically injects CSS variables for theming.
 *
 * Priority for branding:
 * 1. Override branding (for previews)
 * 2. Domain-based org branding (white-label domains)
 * 3. Authenticated user's org branding
 * 4. Default branding
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  ReactNode,
} from "react";
import { useOrganization } from "@/hooks/use-organization";
import { useDomain } from "@/contexts/DomainContext";
import {
  OrganizationBranding,
  BrandingColors,
  DEFAULT_BRANDING,
  CSS_VARIABLE_MAP,
} from "@/lib/branding";

interface BrandingContextType {
  /** Current organization branding settings */
  branding: OrganizationBranding;
  /** Whether branding has been loaded */
  isLoaded: boolean;
  /** The business name to display */
  businessName: string;
  /** Logo URL (or null if not set) */
  logoUrl: string | null;
  /** Get custom terminology */
  getTerm: (key: "session" | "trainer" | "client" | "package") => string;
  /** Whether this org has custom branding enabled */
  hasCustomBranding: boolean;
}

const BrandingContext = createContext<BrandingContextType | undefined>(
  undefined
);

/**
 * Injects CSS variables for branding colors into the document
 */
function injectBrandingCSS(colors: BrandingColors) {
  const root = document.documentElement;

  // Apply each color that's defined
  Object.entries(colors).forEach(([key, value]) => {
    if (value) {
      const cssVar = CSS_VARIABLE_MAP[key as keyof BrandingColors];
      if (cssVar) {
        root.style.setProperty(cssVar, value);
      }
    }
  });
}

/**
 * Removes custom branding CSS variables, restoring defaults
 */
function removeBrandingCSS() {
  const root = document.documentElement;

  // Remove all custom properties we may have set
  Object.values(CSS_VARIABLE_MAP).forEach((cssVar) => {
    root.style.removeProperty(cssVar);
  });
}

interface BrandingProviderProps {
  children: ReactNode;
  /** Optional override branding (useful for previews) */
  overrideBranding?: OrganizationBranding;
}

export function BrandingProvider({
  children,
  overrideBranding,
}: BrandingProviderProps) {
  const { organization, loading: orgLoading } = useOrganization();
  const { organization: domainOrg, isWhiteLabel, loading: domainLoading } = useDomain();

  // Helper to merge branding with defaults
  const mergeWithDefaults = (
    source: Partial<OrganizationBranding> | null | undefined
  ): OrganizationBranding => {
    if (!source) return DEFAULT_BRANDING;

    const typedSource = source as OrganizationBranding;
    return {
      ...DEFAULT_BRANDING,
      ...typedSource,
      colors: {
        ...DEFAULT_BRANDING.colors,
        ...(typedSource.colors || {}),
      },
      email: {
        ...DEFAULT_BRANDING.email,
        ...(typedSource.email || {}),
      },
      links: {
        ...DEFAULT_BRANDING.links,
        ...(typedSource.links || {}),
      },
      terminology: {
        ...DEFAULT_BRANDING.terminology,
        ...(typedSource.terminology || {}),
      },
    };
  };

  // Merge org branding with defaults
  // Priority: override > domain org (white-label) > user's org > defaults
  const branding = useMemo<OrganizationBranding>(() => {
    // If override is provided (e.g., for preview), use it
    if (overrideBranding) {
      return mergeWithDefaults(overrideBranding);
    }

    // For white-label domains, use domain-based org branding
    // This applies even before the user is authenticated
    if (isWhiteLabel && domainOrg?.branding) {
      return mergeWithDefaults(domainOrg.branding as Partial<OrganizationBranding>);
    }

    // Fall back to authenticated user's org branding
    const orgBranding = organization?.settings?.branding;
    if (orgBranding) {
      return mergeWithDefaults(orgBranding as Partial<OrganizationBranding>);
    }

    return DEFAULT_BRANDING;
  }, [organization?.settings?.branding, overrideBranding, isWhiteLabel, domainOrg?.branding]);

  // Inject CSS variables when branding changes
  useEffect(() => {
    if (branding.colors) {
      injectBrandingCSS(branding.colors);
    }

    // Cleanup on unmount
    return () => {
      removeBrandingCSS();
    };
  }, [branding.colors]);

  // Update favicon if custom one is set
  useEffect(() => {
    if (branding.favicon_url) {
      const link = document.querySelector(
        "link[rel='icon']"
      ) as HTMLLinkElement | null;
      if (link) {
        link.href = branding.favicon_url;
      } else {
        const newLink = document.createElement("link");
        newLink.rel = "icon";
        newLink.href = branding.favicon_url;
        document.head.appendChild(newLink);
      }
    }
  }, [branding.favicon_url]);

  // Update document title if business name is set
  useEffect(() => {
    if (branding.business_name && branding.business_name !== "TrainerPulse") {
      // Only update if not on landing page
      if (!window.location.pathname.startsWith("/landing")) {
        document.title = branding.business_name;
      }
    }
  }, [branding.business_name]);

  const businessName = branding.business_name || DEFAULT_BRANDING.business_name!;
  const logoUrl = branding.logo_url;

  const getTerm = (key: "session" | "trainer" | "client" | "package") => {
    return branding.terminology?.[key] || DEFAULT_BRANDING.terminology[key];
  };

  // Check if org has any custom branding (beyond defaults)
  const hasCustomBranding = useMemo(() => {
    // Check domain org branding first (for white-label)
    if (isWhiteLabel && domainOrg?.branding) {
      const typed = domainOrg.branding as OrganizationBranding;
      return !!(
        typed.logo_url ||
        typed.colors?.primary ||
        (typed.business_name && typed.business_name !== "TrainerPulse")
      );
    }

    // Check user's org branding
    const orgBranding = organization?.settings?.branding;
    if (!orgBranding) return false;

    const typed = orgBranding as unknown as OrganizationBranding;
    return !!(
      typed.logo_url ||
      typed.colors?.primary ||
      (typed.business_name && typed.business_name !== "TrainerPulse")
    );
  }, [organization?.settings?.branding, isWhiteLabel, domainOrg?.branding]);

  // Loading is complete when both domain and org loading are done
  // For white-label, we need domain loading to complete
  // For main app, we just need org loading
  const isLoaded = isWhiteLabel ? !domainLoading : !orgLoading;

  const value: BrandingContextType = {
    branding,
    isLoaded,
    businessName,
    logoUrl,
    getTerm,
    hasCustomBranding,
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

/**
 * Hook to access branding context
 */
export function useBranding() {
  const context = useContext(BrandingContext);
  if (context === undefined) {
    throw new Error("useBranding must be used within a BrandingProvider");
  }
  return context;
}
